import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useCampaign } from '../hooks/useCampaigns'
import { useCampaignCharacters } from '../hooks/useCharacter'
import { CampaignRoleProvider, useCampaignRole } from '../hooks/useCampaignRole'
import { PlayerManager } from '../components/campaign/PlayerManager'

export function CampaignPage() {
  const { id } = useParams<{ id: string }>()
  return (
    <CampaignRoleProvider campaignId={id}>
      <CampaignPageContent campaignId={id!} />
    </CampaignRoleProvider>
  )
}

function CampaignPageContent({ campaignId }: { campaignId: string }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isGM } = useCampaignRole()
  const { data: campaign, isLoading: loadingCampaign } = useCampaign(campaignId)
  const { data: snapshots, isLoading: loadingChars } = useCampaignCharacters(campaignId)
  const [tab, setTab] = useState<'pc' | 'npc'>('pc')
  const [npcFilter, setNpcFilter] = useState('')

  // Fetch PC ownership for player assignment
  const { data: pcOwnership } = useQuery({
    queryKey: ['pc-ownership', campaignId],
    enabled: isGM,
    queryFn: async () => {
      const { data } = await supabase
        .from('characters')
        .select('id, name, owner_user_id')
        .eq('campaign_id', campaignId)
        .eq('character_role', 'pc')
      return data ?? []
    },
  })

  // Players with exactly one visible character: redirect straight to their sheet
  useEffect(() => {
    if (!isGM && !loadingChars && snapshots?.length === 1) {
      navigate(`/character/${snapshots[0].character_id}`, { replace: true })
    }
  }, [isGM, loadingChars, snapshots, navigate])

  if (loadingCampaign || loadingChars) return <div style={{ padding: '2rem' }}>Loading...</div>
  if (!campaign) return <div style={{ padding: '2rem' }}>Campaign not found.</div>

  const pcs = snapshots?.filter(s => s.character_role === 'pc') ?? []
  const npcs = snapshots?.filter(s => s.character_role === 'npc') ?? []
  const filteredNpcs = npcFilter
    ? npcs.filter(n => n.character_name.toLowerCase().includes(npcFilter.toLowerCase()) ||
        (n.occupation_name ?? '').toLowerCase().includes(npcFilter.toLowerCase()))
    : npcs

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <button onClick={() => navigate('/dashboard')} style={{ marginRight: '1rem' }}>&larr; Dashboard</button>
          <h1 style={{ display: 'inline', margin: 0 }}>{campaign.name}</h1>
          <span style={{ marginLeft: '1rem', color: '#666' }}>{campaign.setting} — TR {campaign.current_tr_year}</span>
        </div>
        {isGM && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={() => navigate(`/campaign/${campaignId}/generate-npc`)} style={{ padding: '0.5rem 1rem', backgroundColor: '#8b4513', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Generate NPC
            </button>
            <button onClick={() => navigate(`/character/new?campaign=${campaignId}`)} style={{ padding: '0.5rem 1rem' }}>
              + New Character
            </button>
          </div>
        )}
      </div>

      {isGM && pcOwnership && (
        <PlayerManager
          campaignId={campaignId}
          pcs={pcOwnership.map(pc => ({
            character_id: pc.id,
            character_name: pc.name,
            owner_user_id: pc.owner_user_id,
          }))}
          onChanged={() => {
            queryClient.invalidateQueries({ queryKey: ['pc-ownership', campaignId] })
            queryClient.invalidateQueries({ queryKey: ['campaign-characters', campaignId] })
          }}
        />
      )}

      {isGM && (
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <button
            onClick={() => setTab('pc')}
            style={{ padding: '0.5rem 1rem', fontWeight: tab === 'pc' ? 'bold' : 'normal', borderBottom: tab === 'pc' ? '2px solid #333' : 'none' }}
          >
            Player Characters ({pcs.length})
          </button>
          <button
            onClick={() => setTab('npc')}
            style={{ padding: '0.5rem 1rem', fontWeight: tab === 'npc' ? 'bold' : 'normal', borderBottom: tab === 'npc' ? '2px solid #333' : 'none' }}
          >
            NPCs ({npcs.length})
          </button>
        </div>
      )}

      {tab === 'pc' && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #333', textAlign: 'left' }}>
              <th style={{ padding: '0.5rem' }}>Name</th>
              <th>Occupation</th>
              <th>Age</th>
              <th>Penalty</th>
              <th>Wounds</th>
              <th>Funds (d)</th>
            </tr>
          </thead>
          <tbody>
            {pcs.length === 0 && (
              <tr><td colSpan={6} style={{ padding: '1rem', color: '#666' }}>No player characters yet.</td></tr>
            )}
            {pcs.map(pc => (
              <tr
                key={pc.character_id}
                onClick={() => navigate(`/character/${pc.character_id}`)}
                style={{ cursor: 'pointer', borderBottom: '1px solid #eee' }}
              >
                <td style={{ padding: '0.5rem' }}><strong>{pc.character_name}</strong></td>
                <td>{pc.occupation_name ?? '—'}</td>
                <td>{pc.current_age}</td>
                <td style={{ color: pc.physical_penalty > 0 ? 'red' : 'inherit' }}>
                  {pc.physical_penalty > 0 ? pc.physical_penalty : '—'}
                </td>
                <td>{pc.has_active_wounds ? 'Yes' : '—'}</td>
                <td>{pc.funds_denarii}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {tab === 'npc' && (
        <>
          <input
            value={npcFilter}
            onChange={e => setNpcFilter(e.target.value)}
            placeholder="Search NPCs by name or occupation..."
            style={{ width: '100%', padding: '0.5rem', marginBottom: '1rem' }}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #333', textAlign: 'left' }}>
                <th style={{ padding: '0.5rem' }}>Name</th>
                <th>Sex</th>
                <th>Occupation</th>
                <th>Class</th>
                <th>Age</th>
                <th>Wounds</th>
                {isGM && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filteredNpcs.length === 0 && (
                <tr><td colSpan={isGM ? 7 : 6} style={{ padding: '1rem', color: '#666' }}>No NPCs found.</td></tr>
              )}
              {filteredNpcs.map(npc => (
                <tr
                  key={npc.character_id}
                  style={{ cursor: 'pointer', borderBottom: '1px solid #eee' }}
                >
                  <td style={{ padding: '0.5rem' }} onClick={() => navigate(`/character/${npc.character_id}`)}>{npc.character_name}</td>
                  <td onClick={() => navigate(`/character/${npc.character_id}`)}>{npc.sex === 'female' ? 'F' : 'M'}</td>
                  <td onClick={() => navigate(`/character/${npc.character_id}`)}>{npc.occupation_name ?? '—'}</td>
                  <td onClick={() => navigate(`/character/${npc.character_id}`)}>{npc.social_class}</td>
                  <td onClick={() => navigate(`/character/${npc.character_id}`)}>{npc.current_age}</td>
                  <td onClick={() => navigate(`/character/${npc.character_id}`)}>{npc.has_active_wounds ? 'Yes' : '—'}</td>
                  {isGM && (
                    <td>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          if (confirm(`Delete "${npc.character_name}"? This cannot be undone.`)) {
                            supabase.from('characters').delete().eq('id', npc.character_id).then(() => {
                              queryClient.invalidateQueries({ queryKey: ['campaign-characters', campaignId] })
                            })
                          }
                        }}
                        className="px-2 py-0.5 text-xs text-danger border border-danger/30 rounded hover:bg-red-50"
                      >Delete</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}
